import { api } from "@shared/api/client";
import type {
  ClockResponse,
  RrhhClock,
  RrhhData,
  VacationJornada,
} from "../types/rrhh";

export async function getRrhhData(): Promise<RrhhData> {
  const { data } = await api.get<RrhhData>("/rrhh");
  return data;
}

export async function getClockStatus(): Promise<RrhhClock> {
  const { data } = await api.get<RrhhClock>("/rrhh/clock/status");
  return data;
}

export async function clock(payload: {
  action: "entrada" | "salida";
  comentario?: string;
  documento?: File | null;
}): Promise<ClockResponse> {
  const formData = new FormData();

  formData.append("action", payload.action);

  if (payload.comentario?.trim()) {
    formData.append("comentario", payload.comentario.trim());
  }

  if (payload.documento) {
    formData.append("documento", payload.documento);
  }

  const { data } = await api.post<ClockResponse>("/rrhh/clock", formData);
  return data;
}

export async function requestVacation(
  days: Array<{ date: string; jornada: VacationJornada }>,
) {
  const { data } = await api.post("/rrhh/vacations", { days });
  return data;
}

export async function deleteVacationRequest(id: number) {
  const { data } = await api.delete(`/rrhh/vacations/${id}`);
  return data;
}

export async function downloadRrhhPayroll(id: number): Promise<void> {
  const res = await api.get(`/rrhh/payrolls/${id}/download`, {
    responseType: "blob",
  });
  downloadBlob(res.data as Blob, filenameFromResponse(res, `nomina-${id}.pdf`));
}

export async function downloadRrhhContract(id: number): Promise<void> {
  const res = await api.get(`/rrhh/contracts/${id}/download`, {
    responseType: "blob",
  });
  downloadBlob(res.data as Blob, filenameFromResponse(res, `contrato-${id}.pdf`));
}

/**
 * Nombre que envía el servidor en Content-Disposition (p. ej.
 * "nomina-septiembre-2026.pdf"). Si la cabecera no está expuesta por CORS se
 * usa el de reserva, que siempre lleva extensión: sin ella el navegador
 * descarga un archivo que el sistema no sabe abrir.
 */
function filenameFromResponse(
  res: { headers?: Record<string, unknown> },
  fallback: string,
): string {
  const header = res.headers?.["content-disposition"];

  if (typeof header === "string") {
    const match = header.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
    if (match?.[1]) {
      try {
        return decodeURIComponent(match[1].trim());
      } catch {
        return match[1].trim();
      }
    }
  }

  return fallback;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");

  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();

  // Revocar la URL en el mismo tick puede abortar la descarga antes de que el
  // navegador la haya iniciado: se libera pasado un tiempo prudencial.
  setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
}
